import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { MobileLayout } from "@/components/Layout";
import { toast } from "sonner";
import { 
  Plus, 
  MapPin, 
  User, 
  Calendar,
  FileText,
  ClipboardList,
  ChevronRight,
  Loader2,
  Archive,
  RotateCcw,
  FileStack,
  Download,
  Copy,
  Trash2,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ConditionsChantierPanel } from "@/components/ConditionsChantierPanel";
import { useOffline } from "@/lib/OfflineContext";

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    visitor_name: "",
    general_notes: ""
  });
  const [comptesRendus, setComptesRendus] = useState([]);
  const [showCRModal, setShowCRModal] = useState(false);
  const [crSaving, setCrSaving] = useState(false);
  const [crDate, setCrDate] = useState(new Date().toISOString().split('T')[0]);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteCR, setDeleteCR] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { isOffline } = useOffline();

  useEffect(() => { loadData(); }, [projectId]);

  const loadData = async () => {
    try {
      const [projectRes, visitsRes, crsRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/visits?project_id=${projectId}`),
        api.get(`/comptes-rendus?projet_id=${projectId}`)
      ]);
      setProject(projectRes.data);
      setVisits(visitsRes.data);
      setComptesRendus(crsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/visits", { project_id: projectId, ...formData });
      toast.success("Visite creee");
      setShowModal(false);
      setFormData({ date: new Date().toISOString().split('T')[0], visitor_name: "", general_notes: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la creation");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      const res = await api.put(`/projects/${projectId}/archive`);
      toast.success(res.data.message);
      setProject(prev => ({ ...prev, status: res.data.status }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    setArchiving(true);
    try {
      const res = await api.put(`/projects/${projectId}/restore`);
      toast.success(res.data.message);
      setProject(prev => ({ ...prev, status: res.data.status }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setArchiving(false);
    }
  };

  const handleCreateCR = async (e) => {
    e.preventDefault();
    setCrSaving(true);
    try {
      const res = await api.post("/comptes-rendus", {
        projet_id: projectId,
        date_cr: crDate,
        date_prochaine_reunion: ""
      });
      toast.success(`Compte rendu #${res.data.numero} créé`);
      setShowCRModal(false);
      navigate(`/comptes-rendus/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCrSaving(false);
    }
  };

  const handleCRDownloadPDF = async (crId, crNumero, e) => {
    e.stopPropagation();
    setActionLoading(`pdf-${crId}`);
    try {
      const res = await api.get(`/comptes-rendus/${crId}/pdf`, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `CR_${crNumero}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (error) {
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCRDuplicate = async (crId, crNumero, e) => {
    e.stopPropagation();
    setActionLoading(`dup-${crId}`);
    try {
      const res = await api.post(`/comptes-rendus/${crId}/dupliquer`);
      toast.success(`Nouveau CR #${res.data.numero} créé depuis le CR #${crNumero}`);
      navigate(`/comptes-rendus/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la duplication");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCRDelete = async () => {
    if (!deleteCR) return;
    setDeleting(true);
    try {
      await api.delete(`/comptes-rendus/${deleteCR.id}`);
      toast.success(`CR #${deleteCR.numero} supprimé`);
      setDeleteCR(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout title="Chargement..." showBack>
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      </MobileLayout>
    );
  }

  const isArchived = project?.status === "archivé";
  const canArchive = user?.role === "admin_entreprise";
  const canCreateVisit = !isArchived && (user?.role === "admin_entreprise" || user?.role === "conducteur_travaux");

  return (
    <MobileLayout title={project?.name || "Projet"} showBack onBack={() => navigate("/projects")}>
      <div className="px-4 lg:px-6 py-6 space-y-6 animate-fade-in">
        {/* Archived banner */}
        {isArchived && (
          <div className="flex items-center gap-3 bg-zinc-800/60 border border-zinc-700 rounded-xl px-4 py-3" data-testid="archive-banner">
            <Archive size={18} className="text-zinc-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-zinc-300 text-sm font-medium">Chantier archivé</p>
              <p className="text-zinc-500 text-xs">Les visites et observations sont conservées mais le chantier n&apos;apparaît plus dans la liste active.</p>
            </div>
            {canArchive && (
              <Button onClick={handleRestore} disabled={archiving} size="sm" className="btn-secondary text-xs gap-1.5 flex-shrink-0" data-testid="restore-btn">
                {archiving ? <Loader2 size={14} className="animate-spin" /> : <><RotateCcw size={14} /> Restaurer</>}
              </Button>
            )}
          </div>
        )}

        {/* Project Info Card */}
        <div className={`bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3 ${isArchived ? "opacity-70" : ""}`}>
          <div className="flex items-center gap-2 text-zinc-300">
            <MapPin size={16} className="text-orange-500" />
            <span className="text-sm">{project?.address}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-300">
            <User size={16} className="text-orange-500" />
            <span className="text-sm">{project?.client}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-300">
            <Calendar size={16} className="text-orange-500" />
            <span className="text-sm">Debut: {project?.start_date}</span>
          </div>
          {project?.description && (
            <div className="pt-2 border-t border-zinc-800">
              <div className="flex items-start gap-2 text-zinc-400">
                <FileText size={16} className="text-orange-500 mt-0.5" />
                <span className="text-sm">{project.description}</span>
              </div>
            </div>
          )}
          {/* Archive button for admin_entreprise — inside the info card */}
          {canArchive && !isArchived && (
            <div className="pt-3 border-t border-zinc-800">
              <Button onClick={handleArchive} disabled={archiving} variant="outline" className="w-full text-sm text-zinc-400 border-zinc-700 hover:border-zinc-600 hover:text-zinc-300 gap-2" data-testid="archive-btn">
                {archiving ? <Loader2 size={16} className="animate-spin" /> : <><Archive size={16} /> Archiver le chantier</>}
              </Button>
            </div>
          )}
        </div>

        {/* Conditions de démarrage chantier */}
        <ConditionsChantierPanel projetId={projectId} />

        {/* Comptes Rendus Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm text-zinc-400 uppercase tracking-wider">
              Comptes rendus ({comptesRendus.length})
            </h3>
            {canCreateVisit && (
              <Button onClick={() => setShowCRModal(true)} disabled={isOffline} size="sm" className="btn-primary text-xs gap-1.5" data-testid="create-cr-btn" title={isOffline ? "Indisponible hors-ligne" : ""}>
                <Plus size={14} /> Nouveau CR
              </Button>
            )}
          </div>

          {comptesRendus.length === 0 ? (
            <div className="empty-state py-6" data-testid="empty-cr-list">
              <FileStack size={36} className="mx-auto mb-2 text-zinc-600" />
              <p className="text-zinc-400 text-sm mb-1">Aucun compte rendu.</p>
              <p className="text-zinc-600 text-xs mb-3">Créez votre premier CR ou dupliquez un CR existant.</p>
              {canCreateVisit && (
                <Button onClick={() => setShowCRModal(true)} disabled={isOffline} size="sm" className="btn-primary text-xs" data-testid="create-first-cr-btn">
                  <Plus size={14} className="mr-1" /> Créer le premier CR
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {comptesRendus.map((cr) => (
                <div key={cr.id} className="card-interactive rounded-xl overflow-hidden" data-testid={`cr-item-${cr.id}`}>
                  <button
                    onClick={() => navigate(`/comptes-rendus/${cr.id}`)}
                    className="w-full p-4 text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-orange-500 font-semibold">CR #{cr.numero}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            cr.statut === "validé"
                              ? "bg-emerald-500/15 text-emerald-400"
                              : "bg-zinc-800 text-zinc-500"
                          }`}>{cr.statut}</span>
                        </div>
                        <p className="text-zinc-400 text-sm">{cr.date_cr}</p>
                      </div>
                      <ChevronRight size={20} className="text-zinc-600 flex-shrink-0 ml-2" />
                    </div>
                  </button>
                  <div className="flex border-t border-zinc-800/50">
                    <button
                      onClick={(e) => handleCRDownloadPDF(cr.id, cr.numero, e)}
                      disabled={actionLoading === `pdf-${cr.id}` || isOffline}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-zinc-500 hover:text-orange-400 hover:bg-zinc-800/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`cr-pdf-${cr.id}`}
                    >
                      {actionLoading === `pdf-${cr.id}` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                      PDF
                    </button>
                    <div className="w-px bg-zinc-800/50" />
                    <button
                      onClick={(e) => handleCRDuplicate(cr.id, cr.numero, e)}
                      disabled={actionLoading === `dup-${cr.id}` || isOffline}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-zinc-500 hover:text-orange-400 hover:bg-zinc-800/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`cr-dup-${cr.id}`}
                    >
                      {actionLoading === `dup-${cr.id}` ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />}
                      Dupliquer
                    </button>
                    {cr.statut === "brouillon" && (
                      <>
                        <div className="w-px bg-zinc-800/50" />
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteCR(cr); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          data-testid={`cr-delete-${cr.id}`}
                        >
                          <Trash2 size={12} />
                          Supprimer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Visits Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm text-zinc-400 uppercase tracking-wider">
              Visites ({visits.length})
            </h3>
          </div>

          {visits.length === 0 ? (
            <div className="empty-state py-8">
              <ClipboardList size={40} className="mx-auto mb-3 text-zinc-600" />
              <p className="text-zinc-400 mb-4">Aucune visite</p>
              {canCreateVisit && (
                <Button onClick={() => setShowModal(true)} className="btn-primary" data-testid="create-first-visit">
                  <Plus size={18} className="mr-2" /> Nouvelle visite
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {visits.map((visit) => (
                <button
                  key={visit.id}
                  onClick={() => navigate(`/visits/${visit.id}`)}
                  className="card-interactive w-full p-4 text-left"
                  data-testid={`visit-item-${visit.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-orange-500 font-semibold">{visit.date}</span>
                      </div>
                      <p className="text-white font-medium">{visit.visitor_name}</p>
                      {visit.general_notes && (
                        <p className="text-zinc-400 text-sm truncate">{visit.general_notes}</p>
                      )}
                    </div>
                    <ChevronRight size={20} className="text-zinc-600 flex-shrink-0 ml-2" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FAB — only for active projects with create permission */}
      {canCreateVisit && (
        <button onClick={() => setShowModal(true)} className="fab" data-testid="add-visit-fab">
          <Plus size={24} className="text-white" />
        </button>
      )}

      {/* Create Visit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Nouvelle visite</DialogTitle>
            <p className="text-zinc-400 text-sm">Enregistrez une nouvelle visite de chantier</p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Date de visite</Label>
              <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="input-dark" data-testid="visit-date-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Nom du visiteur</Label>
              <Input value={formData.visitor_name} onChange={(e) => setFormData({ ...formData, visitor_name: e.target.value })} placeholder="Jean Martin" required className="input-dark" data-testid="visit-visitor-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Notes generales</Label>
              <textarea value={formData.general_notes} onChange={(e) => setFormData({ ...formData, general_notes: e.target.value })} placeholder="Notes sur la visite..." rows={3} className="input-dark w-full resize-none py-3" data-testid="visit-notes-input" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Annuler</Button>
              <Button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="visit-submit-btn">
                {saving ? <Loader2 className="animate-spin" size={20} /> : "Creer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create CR Modal */}
      <Dialog open={showCRModal} onOpenChange={setShowCRModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Nouveau compte rendu</DialogTitle>
            <p className="text-zinc-400 text-sm">Un nouveau CR sera créé avec les acteurs et lots pré-remplis</p>
          </DialogHeader>
          <form onSubmit={handleCreateCR} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Date du compte rendu</Label>
              <Input type="date" value={crDate} onChange={(e) => setCrDate(e.target.value)} required className="input-dark" data-testid="cr-date-input" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setShowCRModal(false)} className="btn-secondary flex-1">Annuler</Button>
              <Button type="submit" disabled={crSaving} className="btn-primary flex-1" data-testid="cr-submit-btn">
                {crSaving ? <Loader2 className="animate-spin" size={20} /> : "Créer le CR"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete CR Confirmation Dialog */}
      <Dialog open={!!deleteCR} onOpenChange={(open) => { if (!open) setDeleteCR(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-400" />
              Supprimer le CR #{deleteCR?.numero} ?
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm pt-2">
              Cette action est <span className="text-red-400 font-semibold">irréversible</span>.
              Toutes les tâches, acteurs et entreprises liés à ce compte rendu seront définitivement supprimés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteCR(null)}
              className="flex-1 border-zinc-700 text-zinc-400"
              data-testid="cancel-delete-cr-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCRDelete}
              disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white gap-2"
              data-testid="confirm-delete-cr-btn"
            >
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </MobileLayout>
  );
};

export default ProjectDetailPage;
